import {Component, inject} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from '@angular/forms';

import {MaterialModule} from '../../../material.module';
import {ClientObjectSettings} from '../../objects/client-object-settings/client-object-settings.component';
import {GeneralSettingsComponent} from './general-settings/general-settings.component';
import {WidgetSettingsComponent} from './widget-settings/widget-settings.component';
import {createWidgetStylingForm} from './widget-settings/widget-styling-form';

@Component({
  selector: 'app-chatbot-settings-visual-fixture',
  imports: [MaterialModule, ReactiveFormsModule, ClientObjectSettings, GeneralSettingsComponent, WidgetSettingsComponent],
  template: `
    <div class="client-container d-flex flex-column chatbot-settings-visual-fixture">
      <div class="view">
        <mat-tab-group [selectedIndex]="selectedTabIndex">
          <mat-tab [disabled]="true">
            <ng-template mat-tab-label>
              <div class="tab-header-label">
                <span class="category"><small>Chatbot</small></span>
                <span class="object-name">{{ chatbotName }}</span>
              </div>
            </ng-template>
          </mat-tab>
          <mat-tab label="Overview">
            <div class="tab-content">
              <mat-card class="cardWithShadow">
                <mat-card-content class="p-24">
                  <p>Visual fixture overview for local review.</p>
                </mat-card-content>
              </mat-card>
            </div>
          </mat-tab>
          <mat-tab label="Settings">
            <div class="tab-content">
              <app-client-object-settings [clientSettingsForm]="clientSettingsForm" [object]="chatbotFixture"></app-client-object-settings>
              <app-general-settings [settingsForm]="settingsForm"></app-general-settings>
            </div>
          </mat-tab>
          <mat-tab label="Widget Launcher">
            <div class="tab-content">
              <app-widget-settings
                [chatbotId]="chatbotId"
                [clientId]="chatbotFixture.clientId"
                [cookiesToCollect]="[]"
                [settingsForm]="settingsForm"
                [widgetForm]="widgetForm"
                [zIndexControl]="zIndexControl"></app-widget-settings>
            </div>
          </mat-tab>
        </mat-tab-group>
      </div>
    </div>
  `,
  styles: [`
    .chatbot-settings-visual-fixture {
      padding: 24px;
    }

    .tab-content {
      padding: 20px 0;
    }

    @media (max-width: 720px) {
      .chatbot-settings-visual-fixture {
        padding: 16px;
      }
    }
  `]
})
export class ChatbotSettingsVisualFixtureComponent {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly fixtureLogoUrl = 'http://127.0.0.1:4205/assets/images/logos/shine-ai-logo.svg';

  readonly chatbotId = this.route.snapshot.paramMap.get('id') || 'fixture-chatbot';
  readonly fixtureState = this.route.snapshot.queryParamMap.get('chatbotSettingsVisualFixture') || 'settings';
  readonly chatbotName = 'Fixture Chatbot';
  readonly selectedTabIndex = this.fixtureState === 'widget-launcher' ? 3 : 2;

  readonly chatbotFixture = {
    id: this.chatbotId,
    clientId: 'fixture-client',
    name: this.chatbotName,
    description: 'Local visual-review fixture for chatbot settings.',
    image: this.fixtureLogoUrl,
    enabled: true,
    updatedDate: new Date('2026-06-26T00:00:00Z')
  };

  readonly clientSettingsForm = this.fb.group({
    name: [this.chatbotName, Validators.required],
    enabled: [true],
    description: ['Local visual-review fixture for chatbot settings.'],
    image: [this.fixtureLogoUrl, [Validators.pattern('https?://.+')]]
  });

  readonly settingsForm = this.fb.group({
    baseUrl: ['https://example.com', [Validators.required, Validators.pattern('https?://.+')]],
    conversationRetentionDays: [90],
    gptClassificationAndSanitizeQuery: ['Sanitize PII and classify intent before routing.'],
    classificationModel: ['gpt-4.1-mini'],
    gptLuceneQuery: ['Prefer product matches with current inventory and pricing.'],
    assistantInstructions: ['Answer in a concise, merchant-safe tone.'],
    aiProductCardsEnabled: [true],
    aiProductCardsInstruction: ['Use product cards when concrete products are relevant.'],
    usePromptCaching: [true],
    dynamicWidgetText: [false],
    dynamicWidgetTextInstruction: [''],
    chatSummaryPrompt: ['Summarize the last seven days of conversations.'],
    proposeSampleMessagesPrompt: ['Suggest new sample prompts based on recent traffic.'],
    cookiesToCollect: [[]]
  });

  readonly zIndexControl = new FormControl(3001);

  readonly widgetForm = this.fb.group({
    text: ['Talk to us'],
    launcherShape: ['rectangle'],
    styling: createWidgetStylingForm(this.fb),
    popupTailEnabled: [true],
    hoverRevealEnabled: [true],
    positionMode: ['fixed'],
    mobilePopupOnce: [false],
    autoClose: this.fb.group({
      enabled: [true],
      delayMs: [2000],
      devices: this.fb.group({
        desktop: [false],
        tablet: [false],
        mobile: [true]
      }),
      closeOnSameClientReferrer: [true]
    }),
    scrollReveal: this.fb.group({
      enabled: [false],
      thresholdPx: [800],
      devices: this.fb.group({
        desktop: [true],
        tablet: [true],
        mobile: [true]
      })
    }),
    images: this.fb.group({
      aiLogo1: [this.fixtureLogoUrl, [Validators.pattern('https?://.+')]],
      aiLogo2: [this.fixtureLogoUrl, [Validators.pattern('https?://.+')]],
      logoSwapEnabled: [true],
      launcherLogoPadding: ['12px']
    }),
    colors: this.fb.group({
      backgroundColor: ['#FFFFFF'],
      textColor: ['#384258'],
      closeButtonBackground: ['#121413'],
      closeButtonColor: ['#FFFFFF'],
      closeButtonShadow: [''],
      popupShadow: [''],
      triangleShadow: [''],
      launcherBackground: ['transparent'],
      launcherShadow: ['']
    })
  });
}
